// UPPDRAG-53 — Popover-based date picker with ISO week numbers and
// Swedish "röda dagar" highlighted in red.
//
// Drop-in replacement for `<Input type="date" value={...} onChange={...}>`
// in admin forms where week numbers + holiday awareness matter
// (league round planning, season setup, etc.).
//
// Native HTML date inputs don't show week numbers or country-specific
// holidays — admins were eyeballing calendars to figure out which Friday
// was Midsommarafton, which Sunday was Påskdagen, etc. This component
// wraps shadcn `Calendar` (react-day-picker) inside a `Popover` and
// hooks into DayPicker's modifier system to:
//   - Highlight Swedish holidays in red (text-red-500)
//   - Show the holiday name as a Tooltip on hover
//   - Display ISO week numbers in the left column (already on by default
//     in our shadcn Calendar wrapper)

import { useMemo, useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getSwedishHolidaysMap, getSwedishHolidayName } from "@/lib/swedishHolidays";

interface DatePickerWithHolidaysProps {
  /** ISO date string (YYYY-MM-DD) or empty. */
  value: string;
  /** Receives the same ISO format on change, or "" when cleared. */
  onChange: (iso: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Render in a smaller form input slot. */
  className?: string;
  /** Disable dates before this Date (inclusive). */
  fromDate?: Date;
  /** Disable dates after this Date (inclusive). */
  toDate?: Date;
}

/** Convert YYYY-MM-DD ↔ Date in local TZ (no shift). */
function isoToDate(iso: string | null): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function dateToIso(d: Date | undefined): string {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function DatePickerWithHolidays({
  value,
  onChange,
  placeholder = "Välj datum",
  disabled = false,
  className,
  fromDate,
  toDate,
}: DatePickerWithHolidaysProps) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => isoToDate(value) ?? new Date());
  const selectedDate = isoToDate(value);

  // Build a holiday lookup for the visible months — DayPicker calls our
  // modifier function for every rendered cell, so the Map keeps it cheap.
  // Covers prev + current + next year so navigating across year boundaries
  // doesn't lose the styling for a flash before recomputing.
  const holidayMap = useMemo(() => {
    const y = viewMonth.getFullYear();
    const merged = new Map<string, string>();
    for (const yr of [y - 1, y, y + 1]) {
      for (const [k, v] of getSwedishHolidaysMap(yr)) merged.set(k, v);
    }
    return merged;
  }, [viewMonth]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !selectedDate && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, "EEE d MMM yyyy", { locale: sv }) : placeholder}
          {/* UPPDRAG-53: surface the holiday name on the trigger when the
              picked date IS a holiday — fast feedback without opening the
              popover, helps admin spot accidental Långfredag picks etc. */}
          {selectedDate && getSwedishHolidayName(selectedDate) && (
            <span className="ml-auto text-xs text-red-500 truncate max-w-[140px]">
              {getSwedishHolidayName(selectedDate)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <TooltipProvider delayDuration={150}>
          <Calendar
            mode="single"
            selected={selectedDate}
            month={viewMonth}
            onMonthChange={setViewMonth}
            onSelect={(d) => {
              onChange(dateToIso(d));
              setOpen(false);
            }}
            fromDate={fromDate}
            toDate={toDate}
            // UPPDRAG-53: holiday modifier — DayPicker passes the Date back to
            // our matcher; we look up the local YYYY-MM-DD in the precomputed
            // map. A separate modifiersClassNames entry colors the matched
            // cells red. The Tooltip wraps the day cell via DayContent.
            modifiers={{
              holiday: (date: Date) => holidayMap.has(dateToIso(date)),
            }}
            modifiersClassNames={{
              holiday: "text-red-500 font-medium",
            }}
            components={{
              // UPPDRAG-53: render each day inside a Tooltip so hovering a
              // holiday day reveals its name. Non-holiday days get no
              // tooltip wrapper to keep the DOM lean.
              DayContent: ({ date }: { date: Date }) => {
                const name = holidayMap.get(dateToIso(date));
                if (!name) return <span>{date.getDate()}</span>;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{date.getDate()}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {name}
                    </TooltipContent>
                  </Tooltip>
                );
              },
            }}
          />
        </TooltipProvider>
        {selectedDate && (
          <div className="border-t p-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Rensa
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
